import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserStore } from "@/store/userStore";
import { Button } from "@/components/ui/button";
import {
  adminWelcomeMessagesApi,
  type VisibleWelcomeMessage,
} from "@/services/edgeFunctionWrappers/adminWelcomeMessagesWrapper";

/**
 * Shows the super-admin-defined welcome page to gym admins once per app
 * launch / sign-in (sessionStorage is cleared when the app restarts, and the
 * key includes the sign-in timestamp so a reconnection shows it again).
 */
export function AdminWelcomeGate() {
  const { t } = useTranslation("welcome");
  const { user } = useAuth();
  const { gymId, isLoadingGymSwitch } = useUserStore();
  const [messages, setMessages] = useState<VisibleWelcomeMessage[]>([]);

  const seenKey = user && gymId ? `admin_welcome_seen:${user.id}:${gymId}:${user.last_sign_in_at ?? ""}` : null;

  useEffect(() => {
    if (!seenKey || !gymId || isLoadingGymSwitch) return;
    if (sessionStorage.getItem(seenKey)) return;
    let cancelled = false;
    adminWelcomeMessagesApi
      .getForAdmin(gymId)
      .then(({ messages }) => {
        if (!cancelled) setMessages(messages);
      })
      .catch((e) => console.warn("[AdminWelcomeGate] not shown:", e));
    return () => {
      cancelled = true;
    };
  }, [seenKey, gymId, isLoadingGymSwitch]);

  if (messages.length === 0) return null;

  const dismiss = () => {
    if (seenKey) sessionStorage.setItem(seenKey, "1");
    setMessages([]);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/95 backdrop-blur-sm p-4 pt-[calc(env(safe-area-inset-top)+1rem)] pb-[calc(env(safe-area-inset-bottom)+1rem)]"
    >
      <div className="w-full max-w-xl max-h-full flex flex-col rounded-2xl border bg-card text-card-foreground shadow-xl overflow-hidden">
        <div className="bg-primary text-primary-foreground px-6 py-8 text-center">
          <Sparkles className="mx-auto h-10 w-10 mb-3" />
          <p className="text-sm uppercase tracking-widest opacity-90">{t("kicker")}</p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {messages.map((m) => (
            <section key={m.id} className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">{m.title}</h2>
              <p className="whitespace-pre-line text-muted-foreground leading-relaxed">{m.body}</p>
            </section>
          ))}
        </div>
        <div className="border-t px-6 py-4">
          <Button className="w-full min-h-[44px]" onClick={dismiss}>
            {t("continue")}
          </Button>
        </div>
      </div>
    </div>
  );
}
